import Backbone from 'backbone';
import PageApp from 'pageApp';
import _ from 'underscore';
import ObjectUtil from '@component/common/src/util/ObjectUtil';
import { buildUrl, getContextParams } from './util/UrlUtil';
import { Cookies } from './util/Constants';
import {
  parseResponse,
  getAvailability,
  removePriceLabelInTieredPrice,
  getAllTraits,
  getUnselectedTraits,
  getIsUpcAvailableInStore,
  getMasterGroupList,
  formatFinalPrice,
  getBadgeInformation,
  getTopBadges,
  getFinalPrice,
  getPickupDetails,
  SizesType,
  getSizesByType,
} from './util/ProductModelParser';
import {
  andFunctor,
  andTriFunctor,
  imagesAdapter,
} from './util/productModelHelper/PatternsUtils';
import CookieUtil from './util/CookieUtil';
import { getProperty } from './util/PropertyGetter';
import { getLowAvailabilityMessage } from './util/viewState/lowAvailabilityMessage';
import { getCCFPData } from './util/viewState/Ccfp';
import { getKlarnaData } from './util/viewState/Klarna';
import {
  getInitialHexaData,
  getSelectedHexaFrameData,
} from './util/viewState/Hexa';
import { getOffersData } from './util/viewState/Offers';
import {
  getSelectedPriceData,
  getActualPrice,
} from './util/viewState/Price';
import { getViewStateFlags } from './util/viewState/ViewStateUtil';

export default Backbone.Model.extend({

  initialize() {
    const { detail: viewStateDetail } = this.get('viewState') || {};
    // Updates selectedUpcItem to keep it as a "Reference" to actual UPC Object. When one is updated, the other updates as well.
    if (viewStateDetail && viewStateDetail.selectedUpc) {
      viewStateDetail.isEGift = this.get('product').detail.typeName === 'VIRTUAL_GIFT_CARD';
      viewStateDetail.selectedUpcItem = this.get('product').relationships.upcs[viewStateDetail.selectedUpc];
    }
  },
  url() {
    const params = Object.assign({
      _storeLocation: CookieUtil.getNumberCookie(Cookies.bopsPickupInStore, Cookies.miscGCs),
    }, getContextParams(this.get('context')));

    const context = getContextParams(PageApp.getMeta('context'));
    const queryParams = Object.assign(context, params);

    return buildUrl(`/xapi/digital/v1/product/${this.id}`, queryParams);
  },

  parse: parseResponse,

  updateSelectedQuantity(activeQty) {
    let { maxQuantity } = this.get('product').detail;
    const details = this.get('viewState').detail;
    if (details.flags.consolidatedProductComplex && details.selectedUpc) {
      maxQuantity = getProperty(details, 'maxQuantity', 6);
    }
    details.selectedQuantity = activeQty;
    details.isMaximumQuantity = details.selectedQuantity === maxQuantity;
    details.isMinimumQuantity = details.selectedQuantity === 1;

    this.trigger('update:quantity');
  },

  updateSelectTraitCommon(traitName, selectedTraitIndex, selectedTierIndex = 0, additionalData = {}) {
    const isNotMaster = !this.get('product').detail.flags.masterProduct;
    const traitNameLowerCase = traitName.toLowerCase();
    const trait = this.get('viewState').traits[traitNameLowerCase];
    const selectedTrait = this.getSelectedTrait(traitName, selectedTraitIndex, selectedTierIndex);
    if (traitName === 'Color' && additionalData.findationChange) {
      selectedTrait.old.isFindationSelected = false;
      selectedTrait.recent.isFindationSelected = true;
    }

    selectedTrait.old.isSelected = false;
    selectedTrait.recent.isSelected = true;
    trait.selectedSwatchIndex = selectedTraitIndex;
    trait.selectedTierIndex = selectedTierIndex;

    this.get('viewState').detail[`selected${traitName}`] = selectedTrait.recent.id;
    if (isNotMaster) {
      this.updateSelectedUpc();
      this.updateAvailability();
    }
    return {
      isNotMaster,
      selectedTrait,
      trait,
    };
  },

  updateSelectTraitForAccesibility(traitName, selectedTraitIndex, selectedTierIndex = 0) {
    const viewState = this.get('viewState');
    const { detail: viewStateDetail } = viewState;
    const { isNotMaster, selectedTrait } = this.updateSelectTraitCommon(traitName, selectedTraitIndex, selectedTierIndex);

    this.updateSelectedProduct();
    if (traitName === 'Color' || viewStateDetail.flags.consolidatedProductComplex) {
      if (isNotMaster) {
        this.updateSelectedPrice(traitName);
        this.updateSelectedDetails();
        this.updateSelectedFinalPrice(traitName);
      }
      this.updateImagery(traitName);
    }
    if (viewStateDetail.selectedUpc !== undefined) {
      this.trigger('update:selectedUpc');
    }
    this.trigger('update:initNTOView', true);
    viewState.detail[`selected${traitName}Name`] = getProperty(viewStateDetail, `colorMap[${selectedTrait.recent.id}].name`, '');
    this.trigger('update:accesibility:color');
  },

  updateSelectedProduct() {
    if (!this.get('product').detail.flags.consolidatedProductComplex) return;

    const selectedProductId = getProperty(this.get('viewState'), 'detail.selectedUpcItem.relationships.selectedProduct');
    this.get('viewState').detail.selectedProductData = getProperty(this.get('product'), `relationships.memberProductMap[${selectedProductId}]`, this.get('product'));
  },

  clearSelectedSize() {
    const { traits, detail } = this.get('viewState');
    const types = ObjectUtil.getChildProperty(traits, 'type.swatchTiers[0].swatches');
    if (types && types.length > 1) {
      delete detail.selectedSize;
      delete traits.size.selectedSwatchIndex;
      traits.size.swatchTiers[0].swatches.forEach((swatch) => {
        swatch.isSelected = false;
      });
    }
  },

  updateSelectedTrait(traitName, selectedTraitIndex, selectedTierIndex = 0, additionalData = {}) {
    const { detail } = this.get('viewState');
    const isBigTicketItem = getProperty(detail, 'flags.isBigTicketItem', false);
    if (!isBigTicketItem && traitName.toLowerCase() === 'type') {
      this.clearSelectedSize();
    }
    const { isNotMaster } = this.updateSelectTraitCommon(traitName, selectedTraitIndex, selectedTierIndex, additionalData);

    this.updateSelectedProduct();

    if (traitName === 'Color' || detail.flags.consolidatedProductComplex) {
      if (isNotMaster) {
        this.updateSelectedPrice(traitName);
        this.updateSelectedDetails();
        this.updateSelectedFinalPrice(traitName);
        if (this.get('uiKillSwitches').isPDPEnhancementAEnabled) {
          this.updatePriceOfferData();
        }
        this.updateKlarnaPrice();
        this.updateCCFPPrice();
      }
      this.updateImagery(traitName);
    }

    const isFewLeftMessageRedesignEnabled = getProperty(detail, 'flags.isFewLeftMessageRedesignEnabled', false);
    if (isFewLeftMessageRedesignEnabled) {
      this.updateLowAvailabilityMessage();
    }

    this.set(
      'viewStateFlags',
      getViewStateFlags(this.toJSON()),
    );
    if (additionalData.hexaOverlay) {
      this.updateSelectedHexaFrameData();
    }
    if (detail.selectedUpc !== undefined) {
      this.trigger('update:selectedUpc');
    }
    this.trigger('update:initNTOView', true);
    this.trigger(`update:selected${traitName}`);
  },

  updateLowAvailabilityMessage() {
    const viewState = this.get('viewState');
    const message = getLowAvailabilityMessage(viewState);

    viewState.lowAvailability = message;
  },

  updateKlarnaPrice() {
    const { selectedFinalPrice, selectedPrice } = getProperty(this.get('viewState'), 'detail', {});
    const additionalData = {
      featureEligible: this.get('featureEligible'),
      uiKillSwitches: this.get('uiKillSwitches'),
    };

    this.get('viewState').klarna = this.get('uiKillSwitches').isPDPEnhancementLEnabled ?
      getKlarnaData(this.get('viewState').detail.actualPrice, additionalData) :
      getKlarnaData({ selectedFinalPrice, selectedPrice }, additionalData);
  },

  updateCCFPPrice() {
    this.get('viewState').ccfp = getCCFPData(
      this.get('viewState').detail.selectedProductData,
      getProperty(this.get('product'), 'ccfp', {}),
      this.get('viewState').detail.actualPrice,
      { featureEligible: this.get('featureEligible') },
    );
  },

  setInitialHexaData(data) {
    const viewState = this.get('viewState');
    const colorSwatchTiers = getProperty(this.get('viewState'), 'traits.color.swatchTiers', []);
    const colorMap = getProperty(this.get('product'), 'traits.colors.colorMap', {});
    viewState.hexa = getInitialHexaData(data, { colorSwatchTiers, colorMap });
  },

  updateSelectedHexaFrameData() {
    const hexa = getProperty(this.get('viewState'), 'hexa', {});
    const selectedColor = getProperty(this.get('viewState'), 'detail.selectedColor');
    const colorMap = getProperty(this.get('product'), 'traits.colors.colorMap', {});
    hexa.selectedIFrameData = getSelectedHexaFrameData(hexa, { selectedColor, colorMap });
    this.trigger('update:hexaIframe');
  },

  getSelectedTrait(traitName, selectedTraitIndex, selectedTierIndex) {
    const trait = this.get('viewState').traits[traitName.toLowerCase()];
    const oldTraitItems = trait.selectedSwatchIndex !== undefined && trait.swatchTiers[trait.selectedTierIndex].swatches;
    const newTraitItems = trait.swatchTiers[selectedTierIndex].swatches;
    return {
      old: (oldTraitItems && oldTraitItems[trait.selectedSwatchIndex]) || {},
      recent: newTraitItems[selectedTraitIndex] || {},
    };
  },
  updateImagery(traitName = 'Color') {
    const viewState = this.get('viewState');
    const { traits, relationships } = this.get('product');

    const isVideoImageRailEnabled = getProperty(this.get('viewState'), 'detail.flags.isVideoImageRailEnabled', false);
    const consolidatedProductComplex = andFunctor(viewState.detail.flags.consolidatedProductComplex);
    const videoId = getProperty(this.get('product'), 'imagery.videoId', false);
    const pbsUpcNonAvailable = consolidatedProductComplex(this.getNonAvailableUPC(`${viewState.detail.selectedColor}_${viewState.detail.selectedSize}`, 'imageUpc'));
    const nonAvailableUpcId = this.getNonAvailableUPC(`${viewState.detail.selectedColor}_${viewState.detail.selectedSize}`, 'imageUpc');
    if (consolidatedProductComplex(viewState.detail.selectedUpc)) {
      viewState.imagery = getProperty(relationships, `upcs[${viewState.detail.selectedUpc}].imagery`, '');
    } else if (pbsUpcNonAvailable) {
      viewState.imagery = getProperty(relationships, `upcs[${nonAvailableUpcId}].imagery`, '');
    } else {
      if (traitName === 'Color') {
        viewState.imagery = traits.colors.colorMap[viewState.detail.selectedColor].imagery;
      }

      if (traitName === 'Size' && traits.sizes.sizeMap[viewState.detail.selectedSize].imagery) {
        viewState.imagery = traits.sizes.sizeMap[viewState.detail.selectedSize].imagery;
      }
    }

    const imagesInimagery = getProperty(viewState.imagery, 'images.length', 0);
    viewState.imagery.itemQty = isVideoImageRailEnabled ? imagesInimagery + 1 : imagesInimagery;
    viewState.imagery.hasImagesRail = isVideoImageRailEnabled ? imagesInimagery > 0 : imagesInimagery > 1;
    const imagesArray = getProperty(viewState.imagery, 'images', []);
    const applyImageAdapter = andTriFunctor(isVideoImageRailEnabled)(videoId);
    let isVideoinImages = false;
    imagesArray.forEach((image) => {
      if (image.video === true) {
        isVideoinImages = true;
      }
    });
    if (applyImageAdapter(!isVideoinImages)) {
      const [...cloneImages] = imagesArray;
      imagesArray.splice(3, 0, imagesAdapter({ videoId }, cloneImages));
      viewState.imagery.images = imagesArray;
    }
  },
  updateSelectedPrice(traitName = 'Color') {
    const { detail: viewStateDetail } = this.get('viewState');
    const { traits: productTraits, relationships } = this.get('product');

    if (viewStateDetail.flags.consolidatedProductComplex && viewStateDetail.selectedUpc) {
      viewStateDetail.selectedPrice = removePriceLabelInTieredPrice(getProperty(relationships, `upcs[${viewStateDetail.selectedUpc}].pricing`, ''));
    } else {
      if (traitName === 'Color') {
        const { selectedColor } = viewStateDetail;
        viewStateDetail.selectedPrice = removePriceLabelInTieredPrice(getProperty(productTraits, `colors.colorMap[${selectedColor}].pricing`, ''));
      }

      if (traitName === 'Size') {
        const { selectedSize } = viewStateDetail;
        const sizePricing = getProperty(productTraits, `sizes.sizeMap[${selectedSize}].pricing`, '');
        if (sizePricing) {
          viewStateDetail.selectedPrice = removePriceLabelInTieredPrice(sizePricing);
        }
      }
    }
  },

  updateSelectedDetails() {
    const { detail: viewStateDetail } = this.get('viewState');
    const { relationships } = this.get('product');
    const nonAvailableUpcId = this.getNonAvailableUPC(`${viewStateDetail.selectedColor}_${viewStateDetail.selectedSize}`, 'productDescUpc');
    if (viewStateDetail.flags.consolidatedProductComplex && viewStateDetail.selectedUpc) {
      Object.assign(viewStateDetail, getProperty(relationships, `upcs[${viewStateDetail.selectedUpc}].detail`, ''));
    } else if (viewStateDetail.flags.consolidatedProductComplex && nonAvailableUpcId) {
      Object.assign(viewStateDetail, getProperty(relationships, `upcs[${nonAvailableUpcId}].detail`, ''));
    }
  },

  updateGiftCard(giftCardFormData) {
    const { detail: viewStateDetail } = this.get('viewState');
    if (!viewStateDetail.isEGift) {
      this.updateSelectedQuantity(giftCardFormData.giftQuantity); // If Physical gift card, update selected quantity
    }
    this.get('viewState').giftCard = giftCardFormData;
  },

  updateSelectedFinalPrice(traitName = 'Color') {
    const { detail: viewStateDetail, badges: viewStateBadges } = this.get('viewState');
    const product = this.get('product');
    const { traits, relationships } = product;
    const { flags } = product.detail;
    const { flags: { isGiftWithPurchaseItem } } = viewStateDetail;
    const { selectedColor } = viewStateDetail;
    const { selectedSize } = viewStateDetail;
    let selectedFinalPrice;

    if (viewStateDetail.flags.consolidatedProductComplex && viewStateDetail.selectedUpc) {
      selectedFinalPrice = removePriceLabelInTieredPrice(getProperty(relationships, `upcs[${viewStateDetail.selectedUpc}].pricing.price.finalPrice`, ''));
    } else {
      if (traitName === 'Color') {
        selectedFinalPrice = getProperty(traits, `colors.colorMap[${selectedColor}].pricing.price.finalPrice`, '');
      }

      if (traitName === 'Size') {
        const sizeFinalPrice = getProperty(traits, `sizes.sizeMap[${selectedSize}].pricing.price.finalPrice`, '');
        if (sizeFinalPrice) {
          selectedFinalPrice = sizeFinalPrice;
        }
      }
    }

    if (!this.get('uiKillSwitches').isPDPEnhancementJEnabled) {
      formatFinalPrice(selectedFinalPrice);
    }

    const badgeData = getBadgeInformation(product, selectedSize);
    const selectedColorBadge = getProperty(traits, `colors.colorMap[${selectedColor}].pricing.badgeIds`, '');

    let offersRefactored;

    if (!this.get('uiKillSwitches').isPDPEnhancementAEnabled) {
      offersRefactored = getOffersData(badgeData, selectedFinalPrice, selectedColorBadge, {
        featureEligible: this.get('featureEligible'),
      });
    }

    const topBadges = getTopBadges(badgeData, selectedFinalPrice, flags, isGiftWithPurchaseItem, selectedColorBadge, {
      featureEligible: this.get('featureEligible'),
      uiKillSwitches: this.get('uiKillSwitches'),
    });

    viewStateDetail.selectedFinalPrice = getFinalPrice(topBadges, selectedFinalPrice);

    viewStateBadges.topBadges = topBadges;

    if (!this.get('uiKillSwitches').isPDPEnhancementAEnabled) {
      this.get('viewState').offers = offersRefactored;

      if (this.get('featureEligible').priceExp) {
        this.get('viewState').price = getSelectedPriceData(viewStateDetail.selectedPrice, this.get('viewState').offers, {
          featureEligible: this.get('featureEligible'),
        });
      }
    }
  },

  updatePriceOfferData() {
    const {
      selectedColor,
      selectedSize,
      selectedPrice,
    } = getProperty(this.get('viewState'), 'detail', {});
    const offers = getBadgeInformation(this.get('product'), selectedSize);
    const finalPrice = getProperty(selectedPrice, 'price.finalPrice', {});
    const selectedColorOfferIds = getProperty(this.get('product'), `traits.colors.colorMap[${selectedColor}].pricing.badgeIds`, []);

    this.get('viewState').offers = getOffersData(offers, finalPrice, selectedColorOfferIds, {
      featureEligible: this.get('featureEligible'),
    });

    if (this.get('uiKillSwitches').isPDPEnhancementLEnabled) {
      this.get('viewState').detail.actualPrice = getActualPrice(selectedPrice, this.get('viewState').offers);
    }

    this.get('viewState').price = getSelectedPriceData(selectedPrice, this.get('viewState').offers, {
      featureEligible: this.get('featureEligible'),
    });
  },

  updateSelectedUpc() {
    const { detail: viewStateDetail } = this.get('viewState');
    const { traits: productTraits, relationships } = this.get('product');
    const { selectedColor } = viewStateDetail;
    const sizeForKey = viewStateDetail.flags.hasSizes ? `_${viewStateDetail.selectedSize}` : '';
    const typeForKey = viewStateDetail.flags.hasTypes ? `_${viewStateDetail.selectedType}` : '';

    viewStateDetail.selectedUpc = productTraits.traitsMaps.upcMap[selectedColor + sizeForKey + typeForKey];
    viewStateDetail.selectedUpcItem = viewStateDetail.selectedUpc && relationships.upcs[viewStateDetail.selectedUpc];
    viewStateDetail.unselectedTraits = this.getUnselectedTraits().join('/');
    this.updateIsUpcAvailableInStore();
  },

  updateIsUpcAvailableInStore() {
    const { detail: viewStateDetail } = this.get('viewState');
    const { upcs: productUpcs } = this.get('product').relationships;

    viewStateDetail.isUpcAvailableInStore = getIsUpcAvailableInStore(viewStateDetail, productUpcs);
    if (viewStateDetail.isUpcAvailableInStore) {
      viewStateDetail.pickupDetails = getPickupDetails(productUpcs, viewStateDetail);
    }
  },

  updateAvailability() {
    const { detail, traits } = this.get('viewState');
    const availability = getAvailability(this.get('product'), detail.selectedColor, detail.selectedSize, detail.selectedType);

    ['color', 'size', 'type'].forEach((traitName) => {
      if (traits[traitName]) {
        Object.values(traits[traitName].swatchTiers).forEach((tier) => {
          tier.swatches.forEach((swatchItem) => {
            Object.assign(swatchItem, {
              isAvailable: availability[`${traitName}s`][swatchItem.id],
            });
          });
        });
      }
    });
  },
  getNonAvailableUPC(upcMapId, key) {
    const { traits: productTraits } = this.get('product');
    const nonAvailableUpcMap = getProperty(productTraits.traitsMaps, 'nonAvailableUpcMap', '');
    if (nonAvailableUpcMap && nonAvailableUpcMap[upcMapId]) {
      return nonAvailableUpcMap[upcMapId][key];
    }
    return false;
  },
  getAllTraits() {
    return getAllTraits(this.get('viewState'));
  },

  getUnselectedTraits() {
    return getUnselectedTraits(this.get('viewState'));
  },

  updateLatchKey(selectedLatchKeyIndex) {
    const { detail } = this.get('viewState');
    detail.selectedLatchKeyIndex = selectedLatchKeyIndex;
    detail.orderedMasterGroupList = getMasterGroupList(this.get('product'), selectedLatchKeyIndex);
  },

  updateEgiftCardEnabledState() {
    const viewState = this.get('viewState');
    const product = this.get('product');
    if (viewState && product) {
      if (product.detail.flags.giftCard) {
        viewState.featuresViewState.egiftCardEnabled = true;
      }
    }
  },

  selectSplittedSize(type, idx) {
    const viewState = this.get('viewState');
    const { detail } = viewState;
    const compositeSizes = ObjectUtil.getChildProperty(viewState, 'traits.size.swatchTiers[0].swatches');
    const targetSplittedSizes = this.getSizesByType(type);

    const { each } = _;

    // deselect all target sizes, composite sizes
    // eslint-disable-next-line no-param-reassign
    const deselect = (s) => { s.isSelected = false; };
    each(targetSplittedSizes, deselect);
    each(compositeSizes, deselect);
    detail.selectedSize = undefined;

    if (targetSplittedSizes[idx]) {
      // select
      targetSplittedSizes[idx].isSelected = true;

      const selectedNumericSize = this.getSelectedSplittedSize(SizesType.numericSize);
      const selectedLetterSize = this.getSelectedSplittedSize(SizesType.letterSize);

      // update selected sizes
      detail.selectedNumericSize = selectedNumericSize;
      detail.selectedLetterSize = selectedLetterSize;
    }
  },

  findIndexOfCompositeBySplittedSizes(selectedNumericSize, selectedLetterSize) {
    const viewState = this.get('viewState');
    const compositeSizes = ObjectUtil.getChildProperty(viewState, 'traits.size.swatchTiers[0].swatches');
    const { findIndex, intersection } = _;

    if (viewState.traits.size.splittedSizes.letterPresence) {
      if (selectedLetterSize && selectedNumericSize) {
        const inter = intersection(selectedLetterSize.sizeIds, selectedNumericSize.sizeIds);
        const isSizeOfIntersection = s => s.id === inter[0];
        return findIndex(compositeSizes, isSizeOfIntersection);
      }
    }
    return -1;
  },

  getSelectedSplittedSize(type) {
    const { find } = _;
    const targetSplittedSizes = this.getSizesByType(type);
    return find(targetSplittedSizes, this.isSplittedSizeSelected);
  },

  getSizesByType(type) {
    if (this.get('viewState').traits && this.get('viewState').traits.size && this.get('viewState').traits.size.splittedSizes) {
      return getSizesByType(type, this.get('viewState').traits.size.splittedSizes, SizesType);
    }
    return {};
  },
  isSplittedSizeSelected(splittedSize) {
    return splittedSize.isSelected === true;
  },

});



// WEBPACK FOOTER //
// /Users/m802941/WebstormProjects/2020/campaigns/shop-the-look-pdp/node_modules/@page/digital-product-ui-mcom/src/js/ProductModel.js